import React from "react";
import {
  FileText,
  PenTool,
  Mic,
  ClipboardCheck,
  Brain,
  MessageSquare,
  Zap,
  BookOpen,
  Target,
  Briefcase,
  Inbox,
  Eye,
  TrendingUp,
  IndianRupee,
  User,
  Trophy,
  ShieldCheck,
} from "lucide-react";
import TabbedPage from "@/components/app/TabbedPage";
import { useAuth } from "@/contexts/AuthContext";
import { ApplicationsTracker } from "@/pages/app/CampusDrivesPage";
import { myApplications as myCampusApplications } from "@/lib/campusApi";

import ResumeJourneyPage from "@/pages/app/ResumeJourneyPage";
import InterviewPage from "@/pages/app/InterviewPage";
import MockTestPage from "@/pages/app/MockTestPage";
import CoachPage from "@/pages/app/CoachPage";
import CoachPlanPage from "@/pages/app/CoachPlanPage";
import { useSearchParams, Navigate } from "react-router-dom";
import PrepPage from "@/pages/app/PrepPage";
import MatchedJobsPage from "@/pages/app/MatchedJobsPage";
import { JobsBrowse, MyApplications } from "@/pages/app/JobsCandidate";
import ApplicationTransparencyPage from "@/pages/app/ApplicationTransparencyPage";
import ScoreInsightsPage from "@/pages/app/ScoreInsightsPage";
import SalaryPage from "@/pages/app/SalaryPage";
import ProfilePage from "@/pages/app/ProfilePage";
import BadgesPage from "@/pages/app/BadgesPage";

export function ResumeHub() {
  return <ResumeJourneyPage />;
}

/* One "Practice": the course-aware, gradable drills (aptitude, reasoning, verbal,
   core subjects, HR). The old engineer-only "Practice assessment" tab is retired;
   ?tab=practice and ?tab=drills both land here. */
export function InterviewHub() {
  return (
    <TabbedPage
      tabs={[
        { key: "mock", label: "Mock Interview", icon: Mic, render: () => <InterviewPage /> },
        { key: "test", label: "Proctored Test", icon: ShieldCheck, render: () => <MockTestPage /> },
        { key: "practice", label: "Practice", icon: ClipboardCheck, render: () => <PrepPage /> },
        { key: "drills", label: "Practice", icon: ClipboardCheck, render: () => <PrepPage />, hidden: true },
      ]}
    />
  );
}

/* Coach = the one guidance module: journey + chat, skill gaps, learning,
   roadmap, LinkedIn. (Skills/Roadmap used to be separate nav items.) */
export function CoachHub() {
  const [params] = useSearchParams();
  if (params.get("tab") === "linkedin") return <Navigate to="/app/linkedin" replace />;
  // Old keys (career, gaps, learning, roadmap) all live on the plan page now;
  // TabbedPage falls back to the first tab for unknown keys.
  return (
    <TabbedPage
      tabs={[
        { key: "plan", label: "My Coaching Plan", icon: Target, render: () => <CoachPlanPage /> },
        { key: "ask", label: "Ask Coach", icon: Brain, render: () => <CoachPage /> },
      ]}
    />
  );
}

export function JobsHub() {
  return (
    <TabbedPage
      tabs={[
        { key: "foryou", label: "For You", icon: Target, render: () => <MatchedJobsPage /> },
        { key: "all", label: "All Jobs", icon: Briefcase, render: () => <JobsBrowse /> },
      ]}
    />
  );
}

function CampusApplicationsTab() {
  const [apps, setApps] = React.useState(null);
  React.useEffect(() => {
    myCampusApplications().then(({ data }) => setApps(Array.isArray(data) ? data : [])).catch(() => setApps([]));
  }, []);
  if (apps === null) return <div className="container-x py-14 text-stone-500">Loading your campus applications…</div>;
  if (apps.length === 0) {
    return (
      <div className="container-x py-14">
        <div className="rounded-3xl border border-dashed border-stone-300 bg-[#F5F3ED] p-12 text-center text-stone-500">
          You haven&apos;t applied to any campus drives yet.{" "}
          <a href="/app/campus" className="text-[#C2410C] font-medium hover:underline">See your drives →</a>
        </div>
      </div>
    );
  }
  return <div className="container-x pb-14"><ApplicationsTracker apps={apps} /></div>;
}

export function ApplicationsHub() {
  const { user } = useAuth();
  const isCampusStudent = user?.role === "candidate" && !!user?.college_id;
  // Campus students live in drives — their campus applications lead the hub.
  const tabs = isCampusStudent
    ? [
        { key: "campus", label: "Campus Drives", icon: Target, render: () => <CampusApplicationsTab /> },
        { key: "list", label: "Other Applications", icon: Inbox, render: () => <MyApplications /> },
      ]
    : [
        { key: "list", label: "My Applications", icon: Inbox, render: () => <MyApplications /> },
        { key: "standing", label: "Where I Stand", icon: Eye, render: () => <ApplicationTransparencyPage /> },
      ];
  return <TabbedPage tabs={tabs} />;
}

export function ScoreHub() {
  const { user } = useAuth();
  // Campus students don't see the salary projection (often not meaningful for freshers).
  const isCampusStudent = user?.role === "candidate" && !!user?.college_id;
  const tabs = [
    { key: "insights", label: "Score Insights", icon: TrendingUp, render: () => <ScoreInsightsPage /> },
  ];
  if (!isCampusStudent) tabs.push({ key: "salary", label: "Salary", icon: IndianRupee, render: () => <SalaryPage /> });
  return <TabbedPage tabs={tabs} />;
}

export function ProfileHub() {
  return (
    <TabbedPage
      tabs={[
        { key: "profile", label: "Profile", icon: User, render: () => <ProfilePage /> },
        { key: "badges", label: "Badges", icon: Trophy, render: () => <BadgesPage /> },
      ]}
    />
  );
}
